import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect } from "react";
import { useForm } from "react-hook-form";

import { OptionType, useGetOptionsQuery } from "@/api/admin";
import {
  BlockWithBorder,
  CustomCardBody,
  CustomLabel,
  Loader,
  Subtitle,
  Switch,
  Tags,
} from "@/components/atoms";
import {
  RadioSelect,
  SelectOptionsWithValidation,
} from "@/components/molecules";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useWizardStepDraft } from "@/modules/wizardDraft";
import { rootStore } from "@/store/RootStore";
import type { FormProps, FormRefType } from "@/types/common";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";
import { wordToUpper } from "@/utils/wordToUpper";

import type { VibesType } from "../BrandFormVibes";
import {
  NO_DRESS_CODE_PHRASE,
  priceLevelList,
  reservationsList,
  waitTimeList,
} from "./constants";
import { BranchReservationSchema, type BranchReservationType } from "./types";

const mapOptionsWithUpperName = <T extends { name: string }>(list?: T[]) =>
  (list ?? []).map(item => ({
    ...item,
    name: wordToUpper(item.name),
  }));

type BranchReservationFormProps = FormProps<BranchReservationType> & {
  draftEntityId?: string;
};

const BranchReservationForm = forwardRef<
  FormRefType<BranchReservationType>,
  BranchReservationFormProps
>((props, ref) => {
  const {
    defaultValues,
    onSubmit,
    onValidityChange,
    id,
    classes,
    draftEntityId,
  } = props;
  const { isAdmin } = rootStore.authStore;

  const form = useForm<BranchReservationType>({
    resolver: zodResolver(BranchReservationSchema),
    defaultValues,
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  useWizardStepDraft({
    form,
    wizard: "branch",
    entityId: draftEntityId,
    step: "reservations",
  });

  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.Tags,
    OptionType.Ambiance,
    OptionType.DressCode,
    OptionType.Moment,
  ]);

  const dressCodeList = (optionsData?.dressCode ?? []).map(item => {
    const nameLower = item.name.toLowerCase();
    const exclusive = nameLower === NO_DRESS_CODE_PHRASE;

    return {
      ...item,
      name: wordToUpper(item.name),
      exclusive,
    };
  });

  const tagsList = mapOptionsWithUpperName(optionsData?.tags);
  const ambianceList = mapOptionsWithUpperName(optionsData?.ambiance);
  const momentsList = mapOptionsWithUpperName(optionsData?.moment);

  const allVibes: VibesType[] = [
    { title: "Tags", list: tagsList, name: "tags" },
    { title: "Ambiance", list: ambianceList, name: "ambiance" },
    { title: "Dress code", list: dressCodeList, name: "dressCode" },
    { title: "Moments", list: momentsList, name: "moment" },
    { title: "Price range", list: priceLevelList, name: "priceLevel" },
  ];

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
  } = form;

  const reservationRequired = watch("reservationRequired");

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useExposeFormValues(ref, form);

  if (isOptionPending) {
    return <Loader fullscreen />;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        id={id}
        data-testid="branch-reservation-form"
      >
        <CustomCardBody classes={cn(classes)}>
          <BlockWithBorder>
            <div className="max-w-[610px] flex flex-col gap-4.5 mb-4">
              {allVibes.map(list => (
                <Tags
                  key={list.title}
                  tagsList={list.list}
                  title={list.title}
                  form={control}
                  name={list.name}
                  disabled={list.name === "priceLevel" && !isAdmin}
                  multiple={list.name !== "priceLevel"}
                />
              ))}
            </div>
          </BlockWithBorder>

          <div className="flex flex-col gap-5 mt-8">
            <Subtitle classes="mb-1 text-lg" title="Reservations" />
            <FormField
              control={control}
              name="reservationRequired"
              render={({ field }) => (
                <FormItem className="flex items-center">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="branch-reservation-switch"
                    />
                  </FormControl>
                  <CustomLabel
                    htmlFor="reservation"
                    label="Reservation required"
                  />
                </FormItem>
              )}
            />

            <div className="flex gap-5 sm:gap-14 mb-4 flex-col sm:flex-row">
              <SelectOptionsWithValidation
                name="reservationMethods"
                form={control}
                title="Reservation methods:"
                selectList={reservationsList}
                withValidation={reservationRequired}
              />
              <RadioSelect<BranchReservationType>
                name="averageWaitTime"
                form={control}
                title="Average wait time:"
                selectList={waitTimeList}
              />
            </div>
          </div>
        </CustomCardBody>
      </form>
    </Form>
  );
});

BranchReservationForm.displayName = "BranchReservationForm";
export default BranchReservationForm;
